import DocumentMeta from 'react-document-meta';
import React, { useState, useEffect, useMemo } from 'react';
import Styles from './styles.module.css';
import Slider from "./4.strongImage/index";
import Header from "./0.header/index";
import Title from "./title/index";
import Point from "./1.point/index";
import Person from "./6.person/index";
import Number from "./3.number/index";
import Posts from "./7.posts/index";
import InfoSessions from "./8.infoSessions/index";
import Coupons from "./9.coupons/index";
import StaffProfiles from "./10.staffProfiles/index";
import Interview from "./5.interviews/index";
import Stores from "./2.stores/index";
import Employment from "./employment/index";
import { defaultSummaryData, defaultPointData, defaultNumberData, defaultStrongData, defaultInterviewsData, formatSummary, formatPoint, formatNumber, formatStrong, formatInterviews, formatPerson, defaultPersonData, formatInfo, defaultInfo, defaultApplicationInfo, defaultBenefit, defaultEmployment17, defaultEmployment18, defaultEmployment20, defaultPamphlet, defaultTarget, formatEmployment18, formatEmployment20, formatTarget, formatEmployment17, formatApplicationInfo, formatBenefit, formatPamphlet } from "../../../type";
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { JobPostingYearData, loadAllJobPostingYearData } from './jobPostingYearData';
import { JobPostingYearBundle } from './employment/index';

// jobPostings/{year} ドキュメントの年度別フィールドを、同名のフラット本体
// フィールドへフィールド単位でフォールバックさせた「求人票ソース」を作る。
// jobYearData が null（サブコレクション未作成）なら docData をそのまま返す。
function buildJobSource(jobYearData: JobPostingYearData | null, docData: any) {
  if (!jobYearData) return docData;
  return {
    info: jobYearData.info ?? docData.info,
    target: jobYearData.target ?? docData.target,
    benefit: jobYearData.benefit ?? docData.benefit,
    employment20: jobYearData.employment20 ?? docData.employment20,
    employment18: jobYearData.employment18 ?? docData.employment18,
    employment17: jobYearData.employment17 ?? docData.employment17,
    applicationInfo: jobYearData.applicationInfo ?? docData.applicationInfo,
  };
}

function formatJobPostingYearBundle(jobSource: any): JobPostingYearBundle {
  return {
    info: formatInfo(jobSource),
    target: formatTarget(jobSource),
    employment20: formatEmployment20(jobSource),
    employment18: formatEmployment18(jobSource),
    employment17: formatEmployment17(jobSource),
    applicationInfo: formatApplicationInfo(jobSource),
    benefit: formatBenefit(jobSource),
  };
}

const Recruit: React.FC = () => {

  const [ summary, setSummary ] = useState(defaultSummaryData);
  const memoizedSummary = useMemo(() => summary, [summary]);

  const [ point, setPoint ] = useState(defaultPointData);
  const memoizedPoint = useMemo(() => point, [point]);

  const [ number, setNumber ] = useState(defaultNumberData);
  const memoizedNumber = useMemo(() => number, [number]);

  const [ strong, setStrong ] = useState(defaultStrongData);
  const memoizedStrong = useMemo(() => strong, [strong]);

  const [ interviews, setInterviews ] = useState(defaultInterviewsData);
  const memoizedInterviews = useMemo(() => interviews, [interviews]);

  const [ person, setPerson ] = useState(defaultPersonData);
  const memoizedPerson = useMemo(() => person, [person]);

  const [ info, setInfo ] = useState(defaultInfo);
  const memoizedInfo = useMemo(() => info, [info]);

  const [ target, setTarget ] = useState(defaultTarget);
  const memoizedTarget = useMemo(() => target, [target]);

  const [ benefit, setBenefit ] = useState(defaultBenefit);
  const memoizedBenefit = useMemo(() => benefit, [benefit]);

  const [ employment20, setEmployment20 ] = useState(defaultEmployment20);
  const memoizedEmployment20 = useMemo(() => employment20, [employment20]);

  const [ employment18, setEmployment18 ] = useState(defaultEmployment18);
  const memoizedEmployment18 = useMemo(() => employment18, [employment18]);

  const [ employment17, setEmployment17 ] = useState(defaultEmployment17);
  const memoizedEmployment17 = useMemo(() => employment17, [employment17]);

  const [ applicationInfo, setApplicationInfo ] = useState(defaultApplicationInfo);
  const memoizedApplicationInfo = useMemo(() => applicationInfo, [applicationInfo]);

  const [ pamphlet, setPamphlet ] = useState(defaultPamphlet);
  const memoizedPamphlet = useMemo(() => pamphlet, [pamphlet]);

  const [ companyId, setCompanyId ] = useState<string>('');
  const [ recruitStatus, setRecruitStatus ] = useState<boolean>(false);
  const [ recruitExists, setRecruitExists ] = useState<boolean | null>(null);

  const db = getFirestore();

  const [ recruitLink, setRecruitLink ] = useState('');
  const [ isJob, setIsJob ] = useState(false);

  // 卒業年度タブ（2027年卒／2028年卒…）用: 実データのある年度を新しい順に
  // 最大2件、フォーマット済みで保持する。0件（jobPostings未対応の会社、
  // または &job が無い）ならタブは出さず、上の単年度state（jobSource由来）
  // だけの従来表示にフォールバックする。
  const [ jobPostingYearsAvailable, setJobPostingYearsAvailable ] = useState<string[]>([]);
  const [ jobPostingYearData, setJobPostingYearData ] = useState<Record<string, JobPostingYearBundle>>({});

  useEffect(() => {
    const url = window.location.href;
    const [, idPart = ''] = url.split('id=');
    const [id, ...flags] = idPart.split('&');
    setRecruitLink(decodeURIComponent(id));
    setIsJob(flags.includes('job'));
  }, []);

  useEffect(() => {
    if (!recruitLink) return;

    const fetchRecruit = async () => {
      try {
        const docRef = doc(db, 'recruits', recruitLink);

        // 年度別・求人票専有フィールド(info / target / employment20/18/17 /
        // benefit / applicationInfo)は recruits/{recruitLink}/jobPostings/{卒業年度}
        // から読む。求人票を表示しない(&job なし)場合は取得しない。会社概要・
        // 魅力・店舗などフラット本体側のデータは従来通り。
        const [docSnap, allJobYearData] = await Promise.all([
          getDoc(docRef),
          isJob ? loadAllJobPostingYearData(recruitLink) : Promise.resolve({} as Record<string, JobPostingYearData>),
        ]);

        if (docSnap.exists()) {
          const docData = docSnap.data();

          // 実データのある年度を新しい順に並べる。先頭が「最新の入力済み年度」＝
          // 従来の単年度表示・卒業年度タブの初期選択の両方に使う。
          const sortedYears = Object.keys(allJobYearData).sort(
            (a, b) => (parseInt(b, 10) || -1) - (parseInt(a, 10) || -1),
          );

          // サブコレクションにデータがある会社はそちらを優先し、まだ無い会社
          // (新しい求人票編集画面・バックフィル未実施)はフラット本体の同名
          // フィールドにフォールバックする。
          const jobSource = buildJobSource(sortedYears.length > 0 ? allJobYearData[sortedYears[0]] : null, docData);

          // 卒業年度タブ用: 実データのある年度を新しい順に最大2件、それぞれ
          // フォーマット済みで保持する。0件なら従来どおりタブ無しの単年度表示。
          const visibleYears = sortedYears.slice(0, 2);
          const yearMap: Record<string, JobPostingYearBundle> = {};
          for (const year of visibleYears) {
            yearMap[year] = formatJobPostingYearBundle(buildJobSource(allJobYearData[year], docData));
          }
          setJobPostingYearsAvailable(visibleYears);
          setJobPostingYearData(yearMap);

          setRecruitExists(true);
          setCompanyId(docData.companyId);
          setRecruitStatus(docData.recruitStatus === 1);

          setSummary(formatSummary(docData));
          setPoint(formatPoint(docData));
          setNumber(formatNumber(docData));
          setStrong(formatStrong(docData));
          setInterviews(formatInterviews(docData));
          setPerson(formatPerson(docData));
          setInfo(formatInfo(jobSource));
          setTarget(formatTarget(jobSource));
          setEmployment20(formatEmployment20(jobSource));
          setEmployment18(formatEmployment18(jobSource));
          setEmployment17(formatEmployment17(jobSource));
          setApplicationInfo(formatApplicationInfo(jobSource));
          setBenefit(formatBenefit(jobSource));
          setPamphlet(formatPamphlet(docData));
        } else {
          setRecruitExists(false);
        }
      } catch {
        setRecruitExists(false);
      }
    };

    fetchRecruit();
  }, [recruitLink, isJob]);

  const shouldNoindex = recruitExists === false || (recruitExists === true && !recruitStatus);
  const recruitCanonical = recruitLink
    ? `https://memorii-works.com/recruit/id=${encodeURIComponent(recruitLink)}`
    : 'https://memorii-works.com/';

  const meta = {
    title: `${summary.name} | 美容師の新卒・アシスタント採用 求人情報サイト memorii works`,
    description: `${summary.name}の新卒募集サイト！このサロンの特徴は、①${point.title1} ②${point.title2} ③${point.title3}です！サロン見学も募集しているので、ぜひお越しくださいませ！`,
    canonical: recruitCanonical,
    meta: {
      charset: 'utf-8',
      name: {
        robots: shouldNoindex ? 'noindex,nofollow,noarchive,noimageindex' : 'index,follow',
        ...(shouldNoindex ? { googlebot: 'noindex,nofollow,noarchive,noimageindex' } : {}),
        'twitter:card': 'summary_large_image',
        'twitter:title': `${summary.name} | memorii works`,
        'twitter:description': `${summary.name}の新卒募集サイト`,
        'twitter:image': 'https://memorii-works.com/ogp.png',
      },
      property: {
        'og:title': `${summary.name} | memorii works`,
        'og:description': `${summary.name}の新卒募集サイト`,
        'og:url': recruitCanonical,
        'og:type': 'article',
        'og:image': 'https://memorii-works.com/ogp.png',
      },
    },
  };

  return (
    <DocumentMeta {...meta}>
    <>
      <div className={Styles.recruit_background}>

        <div className={Styles.bg_letters} aria-hidden="true">
          <span className={`${Styles.letter} ${Styles.l1}`}>m</span>
          <span className={`${Styles.letter} ${Styles.l2}`}>e</span>
          <span className={`${Styles.letter} ${Styles.l3}`}>m</span>
          <span className={`${Styles.letter} ${Styles.l4}`}>o</span>
          <span className={`${Styles.letter} ${Styles.l5}`}>r</span>
          <span className={`${Styles.letter} ${Styles.l6}`}>i</span>
          <span className={`${Styles.letter} ${Styles.l7}`}>i</span>
          <span className={`${Styles.letter} ${Styles.l8}`}>w</span>
          <span className={`${Styles.letter} ${Styles.l9}`}>o</span>
          <span className={`${Styles.letter} ${Styles.l10}`}>r</span>
          <span className={`${Styles.letter} ${Styles.l11}`}>k</span>
          <span className={`${Styles.letter} ${Styles.l12}`}>s</span>
        </div>

        <div className={Styles.recruit}>

          <Header
            companyId={companyId}
            summary={memoizedSummary}
            recruitStatus={recruitStatus} />

          {recruitStatus && (
          <>
            <div className={Styles.section_gap}></div>
            <Title
              title={"魅力3選"}
              desc={"サロンが思う自社の魅力を3つにまとめて紹介"} />

            <Point point={memoizedPoint} pamphlet={memoizedPamphlet} />
          </>)}

          <div className={Styles.hr}></div>
          <Title
            title={"店舗一覧"}
            desc={"店舗ごとに、最寄り駅や施術の頻度などを掲載"} />

          <Stores companyId={companyId} searchType={""} searchPref={""} />

          {recruitStatus && (
          <>
            <div className={Styles.hr}></div>
            <Title
              title={"数字で見る"}
              desc={"魅力を伝わりやすく数字で表現"} />

            <Number number={memoizedNumber} />

            <div className={Styles.hr}></div>
            <Title
              title={"写真で見る"}
              desc={"魅力を写真で表現"} />

            {[ memoizedStrong.item1, memoizedStrong.item2, memoizedStrong.item3 ].map((item, i) => (
              // 写真も文章も無い項目は出さない(学生アプリと同じ)
              (item.images.length > 0 || item.title.trim() || item.desc.trim()) && (
                <div key={i} className={Styles.image_appeal}>
                  <div className={Styles.photo_label}>
                    <span>PHOTO</span><b>{String(i + 1).padStart(2, "0")}</b>
                  </div>
                  {item.images.length > 0 && <Slider imageName={item.images} number={i + 1} />}
                  {item.title.trim() && <h4>{item.title}</h4>}
                  {item.desc.trim() && <p>{item.desc}</p>}
                </div>
              )
            ))}

            <div className={Styles.hr}></div>
            <Title
              title={"インタビュー"}
              desc={"今、現役で働いている方の声を掲載"} />

            <Interview interviews={memoizedInterviews} />

            <div className={Styles.hr}></div>
            <Title
              title={"求める人物像"}
              desc={"求めている人物像を数値化"} />

            <Person person={memoizedPerson} />
          </>)}

          <InfoSessions companyId={companyId} />
          <Coupons companyId={companyId} />
          <StaffProfiles companyId={companyId} />

          <Posts companyId={companyId} />

          {isJob && (
            <Employment
              info={memoizedInfo}
              target={memoizedTarget}
              benefit={memoizedBenefit}
              employment20={memoizedEmployment20}
              employment18={memoizedEmployment18}
              employment17={memoizedEmployment17}
              applicationInfo={memoizedApplicationInfo}
              jobPostingYearsAvailable={jobPostingYearsAvailable}
              jobPostingYearData={jobPostingYearData} />
          )}

        </div>

      </div>
    </>
    </DocumentMeta>
  );
};

export default Recruit;
